import { useState, useCallback } from 'react';

import { Autocomplete, Button, IconButton, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { toast } from 'react-hot-toast';

import {
  DynamicSchema,
  RequestItemsSchema,
  RequestSchemaStatus,
  TaxReturnFormResponse,
  TaxReturnSummary,
  TaxReturnSummaryKeyAmountsItem,
  PaymentEstimateObject,
  PutApiV5RequestsRequestIdTaxReturnFormIdBody,
  parseApiErrorMessage,
  usePutApiV5RequestsRequestIdTaxReturnFormId,
} from '@liscio/api';
import { useApiClient, useRequestStore, SignedUrlPreview, useSignedUrl, VoucherLink } from '@liscio/common';
import {
  Card,
  Divider,
  Icon,
  Skeleton,
  Stack,
  styled,
  Typography,
} from '@liscio/ui';
import { formatStringDate } from '@liscio/utils';

import {
  BalanceTypeTag,
  BalanceType,
} from '../BalanceTypeDropdown/BalanceTypeDropdown';
import { EditableCurrencyField } from '../EditableCurrencyField/EditableCurrencyField';
import { EditableQuarterInfo } from '../EditableQuarterInfo/EditableQuarterInfo';
import { QUARTERS } from '../EditableQuarterInfo/quarters';
import { getUrlPath } from '../utils';
import { stateOptions } from 'components/Select/option-sets';



// ─── Type augmentations (until swagger.yaml is regenerated & codegen re-run) ─

/** Extends the generated key_amounts item with fields already in swagger_helper but not yet in the generated YAML */
type KeyAmountsItem = TaxReturnSummaryKeyAmountsItem & {
  locality?: string | null;
  is_manual?: boolean;
  supports_localities?: boolean;
  is_secondary?: boolean;
};

/** Extends the generated mutation body with the refund field */
type TaxReturnFormUpdateBody = PutApiV5RequestsRequestIdTaxReturnFormIdBody & {
  refund?: string;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCALITY_STATES = ['PA', 'OH', 'MI'];

/** US States + DC only (no Canadian provinces or US territories) */
const US_STATE_ABBREVIATIONS = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI',
  'WY',
]);

/** Predefined locality options for each state that supports localities */
const LOCALITY_OPTIONS: Record<string, string[]> = {
  MI: [
    'Albion',
    'Battle Creek',
    'Benton Harbor',
    'Big Rapids',
    'Detroit',
    'East Lansing',
    'Flint',
    'Grand Rapids',
    'Grayling',
    'Hamtramck',
    'Highland Park',
    'Hudson',
    'Ionia',
    'Jackson',
    'Lansing',
    'Lapeer',
    'Muskegon',
    'Muskegon Heights',
    'Pontiac',
    'Port Huron',
    'Portland',
    'Saginaw',
    'Springfield',
    'Walker',
  ],
  OH: [
    'Ohio RITA',
    'Ohio CCA',
    'Columbus (City)',
    'Cincinnati (City)',
    'Toledo (City)',
    'Akron (City)',
    'Dayton (City)',
    'Ohio - Other City',
  ],
  PA: [
    'Philadelphia - BIRT (Business Income & Receipts)',
    'Philadelphia - Wage Tax/Earnings',
    'Philadelphia - NPT (Net Profits)',
    'Philadelphia - SIT (School Income)',
    'PA - Other (Generic EIT)',
  ],
};

// Column widths for consistent alignment across rows
const COL_NAME_WIDTH = 18; // theme.spacing multiplier
const COL_VOUCHER_WIDTH = 12; // fixed space reserved for the voucher link
const COL_AMOUNT_WIDTH = 15; // fixed width for the amount column (read-only)
const COL_DELETE_WIDTH = 4; // fixed space reserved for the delete icon

// ─── Styled Components ───────────────────────────────────────────────────────

const StyledCard = styled(Card)(({ theme }) => ({
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
  overflow: 'visible',
}));

const BalanceRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isFederal',
})<{ isFederal?: boolean }>(({ theme, isFederal }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 0),
  gap: theme.spacing(1),
  ...(isFederal && {
    backgroundColor: theme.palette.primary.main + '08',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(1.5),
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    paddingRight: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
  }),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
}));

const BalanceLeft = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'space-between',
  },
}));

const BalanceRight = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'flex-end',
  },
}));

const QuartersContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getBalanceType = (
  balance: KeyAmountsItem
): BalanceType => {
  // Use explicit null/undefined check so that a valid $0 refund is still
  // classified as 'refund' rather than falling through to 'amount_due'.
  return balance.refund_in_cents != null ? 'refund' : 'amount_due';
};

const getNormalizedAmountInCents = (
  balance: KeyAmountsItem
): number => {
  // Use explicit null/undefined check so a $0 refund is not misclassified
  // as amount_due (truthy check would treat 0 as falsy).
  return balance.refund_in_cents != null
    ? balance.refund_in_cents
    : (balance.amount_due_in_cents || 0) * -1;
};

/** Returns a display label. For locality rows nested under their state,
 *  shows just the locality name (e.g. "East Lansing" instead of
 *  "Michigan - East Lansing"). */
const getDisplayLabel = (balance: KeyAmountsItem): string => {
  // Locality rows are nested under their state — show just the locality name
  if (balance.locality) return balance.locality;
  // The backend label already handles "Federal", "Michigan", etc.
  if (balance.label) return balance.label;
  if (balance.jurisdiction === 'US') return 'Federal';
  return `${balance.jurisdiction} Income Tax`;
};

// ─── Sub-component: BalanceRowItem ───────────────────────────────────────────

interface BalanceRowItemProps {
  balance: KeyAmountsItem;
  wfRequestUuid?: string | null;
  isEditable: boolean;
  onBalanceUpdated: (updatedForm: TaxReturnFormResponse) => void;
  onDeleteForm?: (uuid: string) => void;
  indent?: boolean;
  isFederal?: boolean;
}

const BalanceRowItem = ({
  balance,
  wfRequestUuid,
  isEditable,
  onBalanceUpdated,
  onDeleteForm,
  indent = false,
  isFederal = false,
}: BalanceRowItemProps) => {
  const balanceType = getBalanceType(balance);
  const normalizedAmountInCents = getNormalizedAmountInCents(balance);
  const amountInDollars = Math.abs(normalizedAmountInCents) / 100;
  const isRefund = balanceType === 'refund';

  // ── Voucher preview state (firm balance rows only show voucher, not Pay) ──
  // The backend returns rpv_document as null for locality rows, so no
  // client-side filtering is needed here.
  const paymentVoucherToken = balance.rpv_document?.retrieval_token ?? null;
  const [isVoucherPreviewOpen, setIsVoucherPreviewOpen] = useState(false);
  const {
    signedUrlData,
    setRetrievalToken,
    refreshSignedUrl,
    clearSignedUrl,
  } = useSignedUrl();

  const handleVoucherClick = useCallback(async () => {
    if (paymentVoucherToken) {
      setRetrievalToken(paymentVoucherToken);
      await refreshSignedUrl();
      setIsVoucherPreviewOpen(true);
    }
  }, [paymentVoucherToken, setRetrievalToken, refreshSignedUrl]);

  const { mutateAsync: updateTaxBalance, isLoading: isUpdating } =
    usePutApiV5RequestsRequestIdTaxReturnFormId({
      mutation: {
        onSuccess: (data) => {
          toast.success('Tax balance updated successfully');
          onBalanceUpdated(data);
        },
        onError: (error: unknown) => {
          const apiErrorMessage = parseApiErrorMessage(error);
          toast.error(apiErrorMessage || 'Failed to update tax balance');
        },
      },
    });

  const handleSaveAmount = async (newAmountInDollars: number) => {
    if (!wfRequestUuid || !balance.uuid) return;

    const amountStr = Math.abs(newAmountInDollars).toString();

    // Only send the active field so the backend knows which type to preserve.
    await updateTaxBalance({
      requestId: wfRequestUuid,
      id: balance.uuid,
      data: (isRefund
        ? { refund: amountStr }
        : { amount_due: amountStr }) as TaxReturnFormUpdateBody,
    });
  };

  const handleBalanceTypeChange = async (newType: BalanceType) => {
    if (!wfRequestUuid || !balance.uuid) return;

    const amountStr = amountInDollars.toString();

    // Only send the target field so the backend can clear the other,
    // even when the amount is $0.
    try {
      await updateTaxBalance({
        requestId: wfRequestUuid,
        id: balance.uuid,
        data: (newType === 'amount_due'
          ? { amount_due: amountStr }
          : { refund: amountStr }) as TaxReturnFormUpdateBody,
      });
    } catch {
      // Error is handled by mutation onError
    }
  };

  const isManual = balance.is_manual;
  const displayLabel = getDisplayLabel(balance);

  const showDelete = isEditable && isManual && onDeleteForm && balance.uuid;

  return (
    <BalanceRow isFederal={isFederal}>
      <BalanceLeft>
        <Typography
          variant="body2"
          sx={(theme) => ({
            minWidth: theme.spacing(COL_NAME_WIDTH),
            width: theme.spacing(COL_NAME_WIDTH),
            ...(indent && { paddingLeft: theme.spacing(3) }),
            ...(isFederal && { fontWeight: 600 }),
          })}
        >
          {displayLabel}
        </Typography>
      </BalanceLeft>
      <BalanceRight>
        <Stack
          sx={(theme) => ({
            minWidth: theme.spacing(COL_VOUCHER_WIDTH),
            width: theme.spacing(COL_VOUCHER_WIDTH),
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          {paymentVoucherToken && (
            <VoucherLink onClick={handleVoucherClick} />
          )}
        </Stack>
        <BalanceTypeTag
          value={balanceType}
          onChange={handleBalanceTypeChange}
          disabled={isUpdating}
          editable={isEditable}
        />
        {isEditable && (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            $
          </Typography>
        )}
        <EditableCurrencyField
          amountInDollars={amountInDollars}
          isEditable={isEditable}
          isLoading={isUpdating}
          onSave={handleSaveAmount}
          readOnlyDisplay={
            <Typography
              variant="body1"
              sx={(theme) => ({
                fontWeight: 600,
                minWidth: theme.spacing(COL_AMOUNT_WIDTH),
                textAlign: 'right',
              })}
            >
              ${amountInDollars.toLocaleString('en-US', {
                minimumFractionDigits: amountInDollars % 1 !== 0 ? 2 : 0,
                maximumFractionDigits: 2,
              })}
            </Typography>
          }
        />
        <Stack
          sx={(theme) => ({
            minWidth: theme.spacing(COL_DELETE_WIDTH),
            width: theme.spacing(COL_DELETE_WIDTH),
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          {showDelete && (
            <IconButton
              size="small"
              onClick={() => {
                const { uuid } = balance;
                if (uuid) onDeleteForm(uuid);
              }}
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                padding: theme.spacing(0.25),
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              })}
            >
              <Icon icon="close" sx={{ fontSize: '1rem' }} />
            </IconButton>
          )}
        </Stack>
      </BalanceRight>
      {isVoucherPreviewOpen && signedUrlData && (
        <SignedUrlPreview
          retrievalToken={paymentVoucherToken as string}
          signedUrl={signedUrlData.signed_url}
          fileName={signedUrlData.filename!}
          onClose={() => {
            setIsVoucherPreviewOpen(false);
            clearSignedUrl();
          }}
        />
      )}
    </BalanceRow>
  );
};

// ─── Sub-component: AddStateInline ──────────────────────────────────────────
// Inline form for adding a new state tax form with a state dropdown.

interface AddStateInlineProps {
  existingJurisdictions: string[];
  onAdd: (
    jurisdiction: string,
    balanceType: BalanceType,
    amount: number
  ) => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
}

const AddStateInline = ({
  existingJurisdictions,
  onAdd,
  isLoading,
  onCancel,
}: AddStateInlineProps) => {
  const [jurisdiction, setJurisdiction] = useState('');
  const [balanceType, setBalanceType] = useState<BalanceType>('amount_due');
  const [amount, setAmount] = useState(0);

  const availableStates = stateOptions.filter(
    (option) =>
      US_STATE_ABBREVIATIONS.has(option.abbreviation) &&
      !existingJurisdictions.includes(option.abbreviation)
  );

  const handleAdd = async () => {
    if (!jurisdiction) return;
    await onAdd(jurisdiction, balanceType, amount);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={(theme) => ({
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(0.5),
        flexWrap: 'wrap',
      })}
    >
      <Select
        value={jurisdiction}
        onChange={(e: SelectChangeEvent<string>) =>
          setJurisdiction(e.target.value)
        }
        displayEmpty
        size="small"
        disabled={isLoading}
        sx={(theme) => ({
          minWidth: theme.spacing(COL_NAME_WIDTH),
          width: theme.spacing(COL_NAME_WIDTH),
          '& .MuiSelect-select': {
            padding: theme.spacing(1),
            fontSize: theme.typography.body2.fontSize,
          },
        })}
      >
        <MenuItem value="" disabled>
          Select State
        </MenuItem>
        {availableStates.map((option) => (
          <MenuItem key={option.abbreviation} value={option.abbreviation}>
            {option.abbreviation} - {option.full}
          </MenuItem>
        ))}
      </Select>
      <BalanceTypeTag
        value={balanceType}
        onChange={setBalanceType}
        disabled={isLoading}
        editable
      />
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        $
      </Typography>
      <EditableCurrencyField
        amountInDollars={amount}
        isEditable
        isLoading={false}
        onSave={async (val) => setAmount(val)}
        onChange={(val) => setAmount(val)}
        readOnlyDisplay={null}
      />
      <Button
        variant="text"
        size="small"
        onClick={handleAdd}
        disabled={isLoading}
        sx={{ fontWeight: 600, textTransform: 'none', minWidth: 'auto', padding: '2px 8px' }}
      >
        {isLoading ? 'Adding...' : 'Add'}
      </Button>
      <Button
        variant="text"
        size="small"
        color="inherit"
        onClick={onCancel}
        sx={{ textTransform: 'none', minWidth: 'auto', padding: '2px 8px', color: 'text.secondary' }}
      >
        Cancel
      </Button>
    </Stack>
  );
};

// ─── Sub-component: AddLocalityInline ───────────────────────────────────────
// Inline form for adding a locality under a specific state.

interface AddLocalityInlineProps {
  jurisdiction: string;
  existingLocalities: string[];
  onAdd: (
    localityName: string,
    balanceType: BalanceType,
    amount: number
  ) => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
}

const AddLocalityInline = ({
  jurisdiction,
  existingLocalities,
  onAdd,
  isLoading,
  onCancel,
}: AddLocalityInlineProps) => {
  const [localityName, setLocalityName] = useState<string | null>(null);
  const [balanceType, setBalanceType] = useState<BalanceType>('amount_due');
  const [amount, setAmount] = useState(0);

  const localityOptions = (LOCALITY_OPTIONS[jurisdiction] || []).filter(
    (opt) => !existingLocalities.includes(opt)
  );

  const handleAdd = async () => {
    if (!localityName) return;
    await onAdd(localityName, balanceType, amount);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={(theme) => ({
        paddingLeft: theme.spacing(3),
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(0.5),
        flexWrap: 'wrap',
      })}
    >
      <Autocomplete
        autoFocus
        openOnFocus
        size="small"
        options={localityOptions}
        value={localityName}
        onChange={(_e, newValue) => setLocalityName(newValue)}
        disabled={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`Select locality`}
            autoFocus
          />
        )}
        sx={(theme) => ({
          minWidth: theme.spacing(COL_NAME_WIDTH),
          width: theme.spacing(COL_NAME_WIDTH),
          '& .MuiInputBase-input': {
            padding: theme.spacing(0.5),
            fontSize: theme.typography.body2.fontSize,
          },
        })}
      />
      <BalanceTypeTag
        value={balanceType}
        onChange={setBalanceType}
        disabled={isLoading}
        editable
      />
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        $
      </Typography>
      <EditableCurrencyField
        amountInDollars={amount}
        isEditable
        isLoading={false}
        onSave={async (val) => setAmount(val)}
        onChange={(val) => setAmount(val)}
        readOnlyDisplay={null}
      />
      <Button
        variant="text"
        size="small"
        onClick={handleAdd}
        disabled={isLoading}
        sx={{ fontWeight: 600, textTransform: 'none', minWidth: 'auto', padding: '2px 8px' }}
      >
        {isLoading ? 'Adding...' : 'Add'}
      </Button>
      <Button
        variant="text"
        size="small"
        color="inherit"
        onClick={onCancel}
        sx={{ textTransform: 'none', minWidth: 'auto', padding: '2px 8px', color: 'text.secondary' }}
      >
        Cancel
      </Button>
    </Stack>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export type TaxBalancesDesktopContainerProps = {
  balancesItem: RequestItemsSchema;
  paymentEstimatesItem?: RequestItemsSchema;
};

export const TaxBalancesDesktopContainer = ({
  balancesItem,
  paymentEstimatesItem,
}: TaxBalancesDesktopContainerProps) => {
  const apiClient = useApiClient();
  const { request } = useRequestStore();
  const queryClient = useQueryClient();
  const isEditable = request?.status === RequestSchemaStatus.draft;

  // Track which inline forms are open
  const [isAddingState, setIsAddingState] = useState(false);
  const [addingLocalityForJurisdiction, setAddingLocalityForJurisdiction] =
    useState<string | null>(null);

  const balancesReadUrl = (balancesItem.dependency?.object as DynamicSchema | undefined)?.read_url;
  const paymentEstimatesReadUrl =
    (paymentEstimatesItem?.dependency?.object as DynamicSchema | undefined)?.read_url;

  const balancesUrlPath = getUrlPath(balancesReadUrl);
  const paymentEstimatesUrlPath = getUrlPath(paymentEstimatesReadUrl);

  const { data: balancesData, isLoading: balancesLoading } =
    useQuery<TaxReturnSummary>({
      queryKey: ['taxBalances', balancesUrlPath],
      queryFn: () => {
        if (!balancesUrlPath) {
          throw new Error('Read URL is required');
        }
        return apiClient.client
          .get<TaxReturnSummary>(balancesUrlPath)
          .then((res: AxiosResponse<TaxReturnSummary>) => res.data);
      },
      enabled: !!balancesUrlPath,
    });

  const { data: paymentEstimatesData } = useQuery<PaymentEstimateObject[]>({
    queryKey: ['paymentEstimates', paymentEstimatesUrlPath],
    queryFn: () => {
      if (!paymentEstimatesUrlPath) {
        throw new Error('Read URL is required');
      }
      return apiClient.client
        .get<PaymentEstimateObject[]>(paymentEstimatesUrlPath)
        .then((res: AxiosResponse<PaymentEstimateObject[]>) => res.data);
    },
    enabled: !!paymentEstimatesUrlPath && !!paymentEstimatesItem,
  });

  const balancesQueryKey = ['taxBalances', balancesUrlPath];

  /** Immediately update a single item in the cached key_amounts array, then background-refetch. */
  const handleBalanceUpdated = (updatedForm: TaxReturnFormResponse) => {
    queryClient.setQueryData<TaxReturnSummary>(balancesQueryKey, (old) => {
      if (!old?.key_amounts) return old;
      return {
        ...old,
        key_amounts: old.key_amounts.map((item) =>
          item.uuid === updatedForm.uuid
            ? {
                ...item,
                amount_due_in_cents: updatedForm.amount_due_in_cents ?? null,
                refund_in_cents: updatedForm.refund_in_cents ?? null,
                label: updatedForm.label ?? item.label,
              }
            : item
        ),
      };
    });
    queryClient.invalidateQueries({ queryKey: balancesQueryKey });
  };

  /** Immediately add a new form to the cached key_amounts array, then background-refetch. */
  const handleBalanceCreated = (newForm: TaxReturnFormResponse) => {
    queryClient.setQueryData<TaxReturnSummary>(balancesQueryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        key_amounts: [
          ...(old.key_amounts || []),
          newForm as unknown as TaxReturnSummaryKeyAmountsItem,
        ],
      };
    });
    queryClient.invalidateQueries({ queryKey: balancesQueryKey });
  };

  /** Immediately remove a form from the cached key_amounts array, then background-refetch. */
  const handleBalanceDeleted = (formUuid: string) => {
    queryClient.setQueryData<TaxReturnSummary>(balancesQueryKey, (old) => {
      if (!old?.key_amounts) return old;
      return {
        ...old,
        key_amounts: old.key_amounts.filter((item) => item.uuid !== formUuid),
      };
    });
    queryClient.invalidateQueries({ queryKey: balancesQueryKey });
  };

  const handlePaymentEstimateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentEstimates', paymentEstimatesUrlPath] });
  };

  // ── Create form mutation (shared for adding states and localities) ──
  const { mutateAsync: createForm, isLoading: isCreatingForm } = useMutation({
    mutationFn: async (data: {
      jurisdiction: string;
      form_name: string;
      locality?: string;
      amount_due?: string;
      refund?: string;
    }) => {
      const wfUuid = balancesData?.wf_request_uuid;
      if (!wfUuid) throw new Error('Request UUID not found');
      const response = await apiClient.client.post(
        `/api/v5/requests/${wfUuid}/tax_return_form`,
        data
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Tax form added successfully');
      handleBalanceCreated(data as TaxReturnFormResponse);
      setIsAddingState(false);
      setAddingLocalityForJurisdiction(null);
      // A new state-level form may have created a PaymentEstimate on the backend;
      // refetch so the Estimated Payments section reflects it immediately.
      queryClient.invalidateQueries({ queryKey: ['paymentEstimates', paymentEstimatesUrlPath] });
    },
    onError: (error: unknown) => {
      const apiErrorMessage = parseApiErrorMessage(error);
      toast.error(apiErrorMessage || 'Failed to add tax form');
    },
  });

  // ── Delete form mutation ──
  const { mutateAsync: deleteForm } = useMutation({
    mutationFn: async (formUuid: string) => {
      const wfUuid = balancesData?.wf_request_uuid;
      if (!wfUuid) throw new Error('Request UUID not found');
      await apiClient.client.delete(
        `/api/v5/requests/${wfUuid}/tax_return_form/${formUuid}`
      );
    },
    onSuccess: (_data, formUuid) => {
      toast.success('Removed successfully');
      handleBalanceDeleted(formUuid);
      // Removing the last state-level form for a jurisdiction may have deleted
      // its PaymentEstimate on the backend; refetch to keep the UI in sync.
      queryClient.invalidateQueries({ queryKey: ['paymentEstimates', paymentEstimatesUrlPath] });
    },
    onError: (error: unknown) => {
      const apiErrorMessage = parseApiErrorMessage(error);
      toast.error(apiErrorMessage || 'Failed to remove');
    },
  });

  // ── Handlers for inline add forms ──
  const handleAddState = async (
    jurisdiction: string,
    balanceType: BalanceType,
    amount: number
  ) => {
    const amountStr = amount.toString();
    await createForm({
      jurisdiction,
      form_name: `${jurisdiction} Tax`,
      ...(balanceType === 'refund'
        ? { refund: amountStr }
        : { amount_due: amountStr }),
    });
  };

  const handleAddLocality = async (
    localityName: string,
    balanceType: BalanceType,
    amount: number,
    jurisdiction: string
  ) => {
    const amountStr = amount.toString();
    await createForm({
      jurisdiction,
      locality: localityName,
      form_name: `${localityName} Local Tax`,
      ...(balanceType === 'refund'
        ? { refund: amountStr }
        : { amount_due: amountStr }),
    });
  };

  if (balancesLoading) {
    return <Skeleton width="100%" height="100px" />;
  }

  const balances = (balancesData?.key_amounts || []) as KeyAmountsItem[];
  const wfRequestUuid = balancesData?.wf_request_uuid;
  const paymentEstimates = paymentEstimatesData || [];

  // Group balances: primary state forms first, then secondary forms + localities underneath
  const nonLocalityBalances = balances.filter((b) => !b.locality);
  const primaryBalances = nonLocalityBalances
    .filter((b) => !b.is_secondary)
    // Federal (US) always first, then stable secondary sort by jurisdiction
    .sort((a, b) => {
      if (a.jurisdiction === 'US') return -1;
      if (b.jurisdiction === 'US') return 1;
      return a.jurisdiction.localeCompare(b.jurisdiction);
    });

  const secondaryBalances = nonLocalityBalances.filter((b) => b.is_secondary);
  const localityBalances = balances.filter((b) => !!b.locality);

  return (
    <Stack gap={1}>
      <Typography variant="h5" fontWeight={600}>
        Balances Due
      </Typography>
      <StyledCard elevation={0}>
        {primaryBalances.map((balance, index) => {
        // Prefer backend-provided flag; fall back to hard-coded list if missing
        const supportsLocalities = balance.supports_localities ?? LOCALITY_STATES.includes(
          balance.jurisdiction
        );
        // Find secondary forms (e.g. OH SD 100) belonging to this jurisdiction
        const stateSecondaries = secondaryBalances.filter(
          (sb) => sb.jurisdiction === balance.jurisdiction
        );
        // Find localities belonging to this state
        const stateLocalities = supportsLocalities
          ? localityBalances.filter((lb) => lb.jurisdiction === balance.jurisdiction)
          : [];

        // Combine secondaries and localities into one sorted list so the
        // order matches the Review section (which sorts all sub-items by prompt)
        const subItems = [...stateSecondaries, ...stateLocalities].sort(
          (a, b) => getDisplayLabel(a).localeCompare(getDisplayLabel(b))
        );
        return (
          <div key={balance.uuid || index}>
            <BalanceRowItem
              balance={balance}
              wfRequestUuid={wfRequestUuid}
              isEditable={isEditable}
              onBalanceUpdated={handleBalanceUpdated}
              onDeleteForm={(uuid) => deleteForm(uuid)}
              isFederal={balance.jurisdiction === 'US'}
            />

            {subItems.map((sub, sIdx) => (
              <BalanceRowItem
                key={sub.uuid || `sub-${sIdx}`}
                balance={sub}
                wfRequestUuid={wfRequestUuid}
                isEditable={isEditable}
                onBalanceUpdated={handleBalanceUpdated}
                onDeleteForm={(uuid) => deleteForm(uuid)}
                indent
              />
            ))}

            {/* Add Locality link/form for PA, OH, MI */}
            {isEditable && supportsLocalities && (
              <>
                {addingLocalityForJurisdiction === balance.jurisdiction ? (
                  <AddLocalityInline
                    jurisdiction={balance.jurisdiction}
                    existingLocalities={stateLocalities
                      .map((lb) => lb.locality)
                      .filter((l): l is string => !!l)}
                    onAdd={(localityName, balanceType, amount) =>
                      handleAddLocality(
                        localityName,
                        balanceType,
                        amount,
                        balance.jurisdiction
                      )
                    }
                    isLoading={isCreatingForm}
                    onCancel={() => setAddingLocalityForJurisdiction(null)}
                  />
                ) : (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() =>
                      setAddingLocalityForJurisdiction(balance.jurisdiction)
                    }
                    sx={(theme) => ({
                      textTransform: 'none',
                      minWidth: 'auto',
                      paddingLeft: theme.spacing(3),
                      paddingBottom: theme.spacing(0.5),
                      justifyContent: 'flex-start',
                      fontSize: theme.typography.body2.fontSize,
                    })}
                  >
                    + Add Locality
                  </Button>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Add State link/form */}
      {isEditable && (
        <>
          {isAddingState ? (
            <AddStateInline
              existingJurisdictions={primaryBalances.map((b) => b.jurisdiction)}
              onAdd={handleAddState}
              isLoading={isCreatingForm}
              onCancel={() => setIsAddingState(false)}
            />
          ) : (
            <Button
              variant="text"
              size="small"
              onClick={() => setIsAddingState(true)}
              sx={(theme) => ({
                textTransform: 'none',
                minWidth: 'auto',
                paddingTop: theme.spacing(0.5),
                paddingBottom: theme.spacing(0.5),
                justifyContent: 'flex-start',
                fontSize: theme.typography.body2.fontSize,
              })}
            >
              + Add State
            </Button>
          )}
        </>
      )}

      {paymentEstimates.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack gap={1}>
            <Typography variant="h6" fontWeight={600}>
              {paymentEstimates[0]?.q1_due_date
                ? `${formatStringDate({
                    dateString: paymentEstimates[0].q1_due_date,
                    defaultPlaceholder: '',
                    outputFormat: 'y',
                  })} ESTIMATED PAYMENTS`
                : 'ESTIMATED PAYMENTS'}
            </Typography>
            {paymentEstimates.map((paymentEstimate, estimateIndex) => (
              <Stack key={estimateIndex} mb={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {paymentEstimate.jurisdiction}
                  </Typography>
                  {paymentEstimate.payment_url && (
                    <Button
                      variant="text"
                      size="small"
                      endIcon={<Icon icon="chevronRight" />}
                      onClick={() =>
                        window.open(paymentEstimate.payment_url!, '_blank')
                      }
                    >
                      Pay
                    </Button>
                  )}
                </Stack>
                <QuartersContainer>
                  {QUARTERS.map(({ key, title, amountKey, dateKey }) => (
                    <EditableQuarterInfo
                      key={key}
                      title={title}
                      amountInCents={paymentEstimate[amountKey]}
                      dueDate={paymentEstimate[dateKey]}
                      paymentEstimate={paymentEstimate}
                      quarter={key}
                      requestStatus={request?.status}
                      onSuccess={handlePaymentEstimateSuccess}
                    />
                  ))}
                </QuartersContainer>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </StyledCard>
    </Stack>
  );
};
